import { useState, useEffect } from 'react'
import { PageHeader } from '@/components/layout/PageHeader'
import { useDocumentHead } from '@/hooks/use-document-head'
import { LinkEditDialog } from '@/components/settings/LinkEditDialog'
import { KnowledgeBasesTab } from '@/components/settings/KnowledgeBasesTab'
import { PronunciationGuidesTab } from '@/components/settings/PronunciationGuidesTab'
import { LinksTab } from '@/components/settings/LinksTab'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Separator } from '@/components/ui/separator'
import { Link2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { knowledgeApiClient } from '@/lib/api'
import type { KnowledgeBase, KnowledgeBaseWithLinks } from '@super/shared-types'
import { useAppAuth } from '@/contexts/app-auth.context'
import { extractUrls } from '@super/shared-types'

const knowledgeBaseSchema = z.object({
  title: z.string().min(1, 'Title is required').max(500, 'Title too long'),
  body: z.string().min(1, 'Content is required'),
})

type KnowledgeBaseFormValues = z.infer<typeof knowledgeBaseSchema>

type TabValue = 'knowledge-bases' | 'pronunciation-guides' | 'links'

const tabConfig: Record<TabValue, { label: string; buttonText: string }> = {
  'knowledge-bases': {
    label: 'Knowledge bases',
    buttonText: 'Add knowledge base',
  },
  'pronunciation-guides': {
    label: 'Pronunciation Guides',
    buttonText: 'Add pronunciation',
  },
  links: { label: 'Links', buttonText: 'Add Link' },
}

export function KnowledgeSettingsPage() {
  useDocumentHead({
    title: 'Knowledge Settings',
    description:
      'Manage your company knowledge bases, pronunciation guides, and links',
  })

  const { currentCompany, isSuperAdmin } = useAppAuth()
  const [activeTab, setActiveTab] = useState<TabValue>('knowledge-bases')
  const [knowledgeBases, setKnowledgeBases] = useState<
    KnowledgeBaseWithLinks[]
  >([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [editingKb, setEditingKb] = useState<KnowledgeBase | null>(null)
  const [showLinkDialog, setShowLinkDialog] = useState(false)
  const [editingLink, setEditingLink] = useState<string | null>(null)

  const form = useForm<KnowledgeBaseFormValues>({
    resolver: zodResolver(knowledgeBaseSchema),
    defaultValues: {
      title: '',
      body: '',
    },
  })

  // Load knowledge bases
  useEffect(() => {
    if (currentCompany?.id) {
      loadKnowledgeBases()
    }
  }, [currentCompany?.id])

  const loadKnowledgeBases = async () => {
    if (!currentCompany?.id) return

    try {
      setLoading(true)
      const data = isSuperAdmin
        ? await knowledgeApiClient.getKnowledgeBasesForCompany(
            currentCompany.id,
          )
        : await knowledgeApiClient.getKnowledgeBases()
      setKnowledgeBases(data)
    } catch (error) {
      console.error('Error loading knowledge bases:', error)
      toast.error('Failed to load knowledge bases')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingKb(null)
    form.reset({ title: '', body: '' })
    setShowDialog(true)
  }

  const handleLinkEdit = (url: string) => {
    setEditingLink(url)
    setActiveTab('links')
    setShowLinkDialog(true)
  }

  const handleEdit = (kb: KnowledgeBase) => {
    setEditingKb(kb)
    form.reset({ title: kb.title, body: kb.body })
    setShowDialog(true)
  }

  const onSubmit = async (data: KnowledgeBaseFormValues) => {
    if (!currentCompany?.id) return

    try {
      setSaving(true)

      if (editingKb) {
        // Update existing
        if (isSuperAdmin) {
          await knowledgeApiClient.updateKnowledgeBaseForCompany(
            editingKb.id,
            currentCompany.id,
            data,
          )
        } else {
          await knowledgeApiClient.updateKnowledgeBase(editingKb.id, data)
        }
        toast.success('Knowledge base updated successfully')
      } else {
        // Create new
        if (isSuperAdmin) {
          await knowledgeApiClient.createKnowledgeBaseForCompany(
            currentCompany.id,
            data,
          )
        } else {
          await knowledgeApiClient.createKnowledgeBase(data)
        }
        toast.success('Knowledge base created successfully')
      }

      setShowDialog(false)
      loadKnowledgeBases()
    } catch (error) {
      console.error('Error saving knowledge base:', error)
      toast.error('Failed to save knowledge base')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6 p-6 pt-0">
      <PageHeader title="Manage your company knowledge and resources" />

      <Tabs
        value={activeTab}
        onValueChange={value => setActiveTab(value as TabValue)}
      >
        <div className="space-y-4 lg:space-y-0">
          <div className="flex flex-col space-y-4 lg:flex-row lg:items-center lg:justify-between lg:space-y-0">
            <TabsList className="grid grid-cols-3 mb-4 lg:mb-0">
              <TabsTrigger value="knowledge-bases">
                {tabConfig['knowledge-bases'].label}
              </TabsTrigger>
              <TabsTrigger value="pronunciation-guides">
                {tabConfig['pronunciation-guides'].label}
              </TabsTrigger>
              <TabsTrigger value="links">
                {tabConfig['links'].label}
              </TabsTrigger>
            </TabsList>
            <Button onClick={handleCreate} variant="form">
              {tabConfig[activeTab].buttonText}
            </Button>
          </div>
        </div>

        <TabsContent value="knowledge-bases" className="mt-6">
          <KnowledgeBasesTab
            knowledgeBases={knowledgeBases}
            loading={loading}
            onEdit={handleEdit}
            onLinkEdit={handleLinkEdit}
            onReload={loadKnowledgeBases}
          />
        </TabsContent>

        <TabsContent value="pronunciation-guides" className="mt-6">
          <PronunciationGuidesTab />
        </TabsContent>

        <TabsContent value="links" className="mt-6">
          <LinksTab
            knowledgeBases={knowledgeBases}
            loading={loading}
            onLinkEdit={handleLinkEdit}
          />
        </TabsContent>
      </Tabs>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingKb ? 'Edit knowledge base' : 'Create knowledge base'}
            </DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter knowledge base title"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="body"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Enter your business rules, procedures, and information. URLs will be automatically detected and extracted."
                        className="min-h-[200px]"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Link preview */}
              {form.watch('body') &&
                extractUrls(form.watch('body')).length > 0 && (
                  <div>
                    <Separator className="my-4" />
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">Detected Links:</h4>
                      <div className="flex flex-wrap gap-2">
                        {extractUrls(form.watch('body')).map(
                          (url: string, index: number) => (
                            <Badge
                              key={index}
                              variant="secondary"
                              className="text-xs"
                            >
                              <Link2 className="h-3 w-3 mr-1" />
                              {url}
                            </Badge>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving
                    ? 'Saving...'
                    : editingKb
                      ? 'Update knowledge base'
                      : 'Create knowledge base'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Link Edit Dialog */}
      <LinkEditDialog
        open={showLinkDialog}
        onOpenChange={setShowLinkDialog}
        link={editingLink}
      />
    </div>
  )
}
